import { getEventList } from "api/event";
import CustomReactSelect from "components/CustomReactSelect";
import BaseTable from "components/DataTable/BaseTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useFilterStore from "store/useFilterStore";
import { formatDateTime, STATES, STATUS_ACTIVE } from "utils/helper";

const TABLE_COLUMNS = [
    { id: "name", Header: "Name", accessor: "name", align: "left", sortable: true },
    { id: "start_date", Header: "Start Date", accessor: "start_date", align: "left" },
    { id: "end_date", Header: "End Date", accessor: "end_date", align: "left" },
    { id: "location", Header: "Location", accessor: "location", align: "left", sortable: true },
    { id: "state", Header: "State", accessor: "state", align: "left", sortable: true },
    { id: "ride", Header: "Number of Ride", accessor: "ride", align: "left" },
    { id: "created_at", Header: "Created At", accessor: "created_at", align: "left", sortable: true },
    { id: "status", Header: "Status", accessor: "status", align: "left" },
];

export const parentRoute = '/events';

function EventTable() {
    const navigate = useNavigate();
    const location = useLocation();
    const { getFilters, setFilters } = useFilterStore();
    const [refreshKey] = useState(0);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const currentPath = window.location.pathname;
    const savedState = location.state || { sort: "created_at", dir: "desc" };

    useEffect(() => {
        const record = getFilters(currentPath);
        if (record?.customParams) {
            if( record.customParams.selectedMonth !== undefined) setSelectedMonth(record.customParams.selectedMonth);
            if( record.customParams.selectedYear !== undefined) setSelectedYear(record.customParams.selectedYear);
            if( record.customParams.selectedState !== undefined) setSelectedState(record.customParams.selectedState);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist custom filters whenever they change (merge with existing baseTable state)
    useEffect(() => {
        const record = getFilters(currentPath);
        setFilters(currentPath, {
            baseTable: record.baseTable, // preserve the table paging/sort managed by BaseTable
            customParams: { selectedMonth, selectedYear, selectedState }
        });
    }, [selectedMonth, selectedYear, selectedState, currentPath]);

    const createTableRow = useCallback((item) => ({
        name: <MDTypography component="div" variant="standard" color="text">{item.name}</MDTypography>,
        start_date: <MDTypography component="div" variant="standard" color="text">
            {new Date(item.start_date).toLocaleDateString()}
        </MDTypography>,
        end_date: <MDTypography component="div" variant="standard" color="text">
            {new Date(item.end_date).toLocaleDateString()}
        </MDTypography>,
        location: <MDTypography component="div" variant="standard" color="text">{item.location}</MDTypography>,
        state: <MDTypography component="div" variant="standard" color="text">{item.state?.name}</MDTypography>,
        ride: <MDTypography component="div" variant="standard" color="text">{item.rides?.length || 0}</MDTypography>,
        created_at: <MDTypography component="div" variant="standard" color="text">
            {formatDateTime(item.created_at)}
        </MDTypography>,
        status: <MDTypography component="div" variant="standard" color={item.status?.id === STATUS_ACTIVE ? "success" : "error"}>
            {item.status?.name}
        </MDTypography>
    }), []);

    const fetchWithFilters = useCallback(async (params = {}) => {
        const filterParams = {
            ...params,
            ...(selectedMonth ? { month: selectedMonth } : {}),
            ...(selectedYear ? { year: selectedYear } : {}),
            ...(selectedState ? { state: selectedState } : {}),
        };
        return getEventList(filterParams);
    }, [selectedMonth, selectedYear, selectedState]);

    const MONTH_OPTIONS = [
        { value: 1, label: "January" },
        { value: 2, label: "February" },
        { value: 3, label: "March" },
        { value: 4, label: "April" },
        { value: 5, label: "May" },
        { value: 6, label: "June" },
        { value: 7, label: "July" },
        { value: 8, label: "August" },
        { value: 9, label: "September" },
        { value: 10, label: "October" },
        { value: 11, label: "November" },
        { value: 12, label: "December" },
    ];

    const currentYear = new Date().getFullYear();
    const YEAR_OPTIONS = Array.from({ length: currentYear - 2021 + 1 }, (_, i) => ({
        value: currentYear - i,
        label: (currentYear - i).toString(),
    }));

    const filters = (
        <>
            {/* Month Filter */}
            <MDBox sx={{ flex: 1, minWidth: 120 }}>
                <CustomReactSelect
                    label="Month"
                    name="month"
                    placeholder="All Months"
                    value={MONTH_OPTIONS.find(month => month.value === selectedMonth)}
                    onChange={option => setSelectedMonth(option?.value)}
                    options={MONTH_OPTIONS}
                    isClearable
                />
            </MDBox>

            {/* Year Filter */}
            <MDBox sx={{ flex: 1, minWidth: 120 }}>
                <CustomReactSelect
                    label="Year"
                    name="year"
                    placeholder="All Years"
                    value={YEAR_OPTIONS.find(year => year.value === selectedYear)}
                    onChange={option => setSelectedYear(option?.value)}
                    options={YEAR_OPTIONS}
                    isClearable
                />
            </MDBox>
            <MDBox sx={{ flex: 1, minWidth: 120 }}>
                <CustomReactSelect
                    label="State"
                    name="state"
                    placeholder="All States"
                    value={STATES.find(state => state.value === selectedState)}
                    onChange={option => setSelectedState(option?.value)}
                    options={STATES}
                    isClearable
                />
            </MDBox>
        </>
    );

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <BaseTable
                key={refreshKey}
                title="Events Management"
                columns={TABLE_COLUMNS}
                fetchData={fetchWithFilters}
                defaultState={savedState}
                createTableRow={createTableRow}
                showAddButton
                addButtonText="Add Event"
                onAddClick={() => navigate(`${parentRoute}/add-event`, { state: savedState })}
                showSearch
                showStatusFilter
                searchPlaceholder="Search event name..."
                additionalFilters={filters}
                onClickRow={(row, key) => { navigate(`${parentRoute}/${row.id}`, { state: savedState }) }}
            />
        </DashboardLayout>
    );
}

export default EventTable;